import { apiService } from './api';

export interface BillingGateResult {
  allowed: boolean;
  reason?: string;
  redirectTo?: '/billing';
  subscriptionStatus?: string;
  mandateStatus?: string;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
}

export interface BillingSubscriptionSummary {
  id: string;
  status: string;
  mandateStatus: string;
  unitsCount: number;
  trialEndsAt: string | null;
  nextBillingDate: string | null;
  graceEndsAt: string | null;
  plan: {
    id: string;
    pricePerUnit: number;
    currency: string;
    trialDays: number;
    graceDays: number;
  } | null;
  bandLabel?: string | null;
  isCustomPricing?: boolean;
  pricingBreakdown?: {
    units: number;
    rate: number;
    subtotal: number;
  }[];
}

export interface BillingStatusResponse {
  gate: BillingGateResult;
  subscription: BillingSubscriptionSummary | null;
  walletBalance: number;
  monthlyFee: number | null;
  isCustomPricing?: boolean;
}

export interface BillingInvoiceItem {
  id: string;
  subscriptionId: string;
  unitsCount: number;
  pricePerUnit: number;
  totalAmount: number;
  status: string;
  dueDate: string;
  paidAt: string | null;
}

export interface BillingInvoicesResponse {
  invoices: BillingInvoiceItem[];
}

export interface BillingRenewalInitiationResponse {
  success: boolean;
  data: {
    invoiceId: string;
    transactionId: string;
    apiRef: string;
    amount: number;
    phone: string;
    status: 'PENDING';
  };
}

export interface BillingRenewalStatusResponse {
  success: boolean;
  data: {
    invoiceId: string;
    status: 'PENDING' | 'PAID' | 'FAILED';
    totalAmount: number;
    dueDate: string;
    paidAt: string | null;
    gate: BillingGateResult;
  };
}

export interface BillingRenewalPreviewResponse {
  success: boolean;
  data: {
    invoiceId: string;
    totalAmount: number;
    unitsCount: number;
    pricePerUnit: number;
    actualUnits: number;
    usedMinimum: boolean;
    bandLabel?: string | null;
    isCustomPricing?: boolean;
    breakdown?: {
      units: number;
      rate: number;
      subtotal: number;
    }[];
    status: string;
    dueDate: string;
    subscriptionStatus: string;
    mandateStatus: string;
  };
}

export const billingService = {
  async getStatus(): Promise<BillingStatusResponse> {
    const res = await apiService.get<BillingStatusResponse>('/billing/status');
    return res.data;
  },

  async getInvoices(limit?: number): Promise<BillingInvoicesResponse> {
    const params = limit != null ? { limit } : {};
    const res = await apiService.get<BillingInvoicesResponse>('/billing/invoices', { params });
    return res.data;
  },

  async activate(termsVersion?: string): Promise<{ success: boolean; subscription: BillingSubscriptionSummary | null; gate: BillingGateResult }> {
    const res = await apiService.post<{ success: boolean; subscription: BillingSubscriptionSummary | null; gate: BillingGateResult }>(
      '/billing/activate',
      { termsVersion: termsVersion ?? '1.0' }
    );
    return res.data;
  },

  async initiateRenewal(phone: string, termsVersion?: string, invoiceId?: string): Promise<BillingRenewalInitiationResponse> {
    const res = await apiService.post<BillingRenewalInitiationResponse>('/billing/renew/initiate', {
      phone,
      termsVersion: termsVersion ?? '1.0',
      invoiceId,
    });
    return res.data;
  },

  async getRenewalPreview(): Promise<BillingRenewalPreviewResponse> {
    const res = await apiService.get<BillingRenewalPreviewResponse>('/billing/renew/preview');
    return res.data;
  },

  async getRenewalStatus(invoiceId: string, transactionId?: string): Promise<BillingRenewalStatusResponse> {
    const params = transactionId ? { transactionId } : undefined;
    const res = await apiService.get<BillingRenewalStatusResponse>(`/billing/renew/status/${invoiceId}`, { params });
    return res.data;
  },

  async getWalletBalance(): Promise<{ balance: number }> {
    const res = await apiService.get<{ balance: number }>('/billing/wallet-balance');
    return res.data;
  },
};
